import { ExtractDateYmd } from "./dateHelper";

/**
 * Normalizes scalar or collection tag values.
 *
 * @param {string|Array<string>|null} value Tag value.
 * @returns {Array<string>} Unique normalized tags.
 */
export function NormalizeTags(value) {
    let source = value;

    if (
        typeof source === "string" &&
        source.trim().startsWith("[")
    ) {
        try {
            source = JSON.parse(source);
        } catch (error) {
            source = [source];
        }
    }

    if (!Array.isArray(source)) {
        source =
            source === null ||
            source === undefined ||
            source === ""
                ? []
                : [source];
    }

    return [
        ...new Set(
            source
                .map((tag) => String(tag || "").trim())
                .filter(Boolean)
        ),
    ];
}

/**
 * Normalizes the canonical park-redemption contract used by album and unlock flows.
 *
 * @param {Object|null} value Public park-redemption state.
 * @returns {{Limit:number,Used:number,Remaining:number,SelectedParks:Array<number>}|null}
 */
export function NormalizeParkRedemption(value) {
    if (!value || typeof value !== "object") {
        return null;
    }

    const selectedParks = [
        ...new Set(
            (Array.isArray(value.SelectedParks)
                ? value.SelectedParks
                : []
            )
                .map((parkId) => Number(parkId))
                .filter(
                    (parkId) => Number.isInteger(parkId) && parkId > 0
                )
        ),
    ];
    const limit = Math.max(0, Math.trunc(Number(value.Limit) || 0));
    const used = selectedParks.length;

    return {
        Limit: limit,
        Used: used,
        Remaining: Math.max(0, limit - used),
        SelectedParks: selectedParks,
    };
}

/**
 * Converts the public redemption object into the internal SUF structure.
 *
 * @param {Object|null} redemption Public redemption details.
 * @returns {Object|null} Internal redemption details.
 */
export function NormalizeRedemption(redemption) {
    if (!redemption) {
        return null;
    }

    return {
        Limit: Number(redemption.Limit || 0),
        Selected: Number(redemption.Used || 0),
        Available: Number(
            redemption.Remaining || 0
        ),
    };
}

/**
 * Normalizes the quoting configuration attached to one album experience.
 *
 * Laravel may serialize an empty JSON object as an empty array. Both shapes
 * represent an experience without a custom quotation and become an object.
 *
 * @param {Object|Array|null} value Public quoting configuration.
 * @returns {Object} Normalized quoting configuration.
 */
export function NormalizeQuotingService(value) {
    if (
        !value ||
        typeof value !== "object" ||
        Array.isArray(value)
    ) {
        return {};
    }

    return { ...value };
}

/**
 * Checks whether an album experience contains an explicit quotation service.
 *
 * @param {Object|Array|null} value Quoting service configuration.
 * @returns {boolean} True when the configuration contains at least one field.
 */
export function HasQuotingService(value) {
    return Object.keys(NormalizeQuotingService(value)).length > 0;
}

/**
 * Normalizes a public album experience for existing SUF components.
 *
 * @param {Object} experience Public album experience.
 * @param {Object|null} service Parent sale or event service details.
 * @returns {Object} Internal album experience.
 */
export function NormalizeAlbumExperience(
    experience = {},
    service = null
) {
    const normalizedService =
        service &&
        typeof service === "object" &&
        !Array.isArray(service)
            ? {
                Id: service.Id ?? null,
                Name: service.Name ?? null,
            }
            : null;
    const quoting = NormalizeQuotingService(
        experience.QuotingService
    );
    const experienceServiceId =
        quoting.Default || normalizedService?.Id || null;

    return {
        ...experience,
        VisitDate: ExtractDateYmd(
            experience.VisitDate
        ),
        ExpiresDate: ExtractDateYmd(
            experience.ExpiresDate
        ) || null,
        AlbumType: Number(
            experience.ContentType || 0
        ),
        AlbumMode: Number(
            experience.RedemptionMode || 0
        ),
        DetailsServiceMedia:
            NormalizeRedemption(
                experience.Redemption
            ),
        QuotingService: quoting,
        Service: normalizedService,
        ServiceId: experienceServiceId,
        ServiceName: normalizedService?.Name || null,
        Tags: [],
    };
}

/**
 * Normalizes the GetAlbums response while preserving the new public fields.
 *
 * @param {Array<Object>} collections Public GetAlbums collections.
 * @returns {Array<Object>} Collections ready for SUF components.
 */
export function NormalizeAlbumCollections(
    collections = []
) {
    return (
        Array.isArray(collections)
            ? collections
            : []
    ).map((collection) => {
        const details =
            collection?.PhotoDetails || {};

        const service =
            details.Service &&
            typeof details.Service === "object" &&
            !Array.isArray(details.Service)
                ? {
                    Id: details.Service.Id ?? null,
                    Name: details.Service.Name ?? null,
                }
                : null;

        return {
            ...collection,
            PhotoDetails: {
                ...details,
                ParkRedemption: NormalizeParkRedemption(
                    details.ParkRedemption
                ),
                Service: service,
                ServiceId:
                    service?.Id || null,
                ServiceName:
                    service?.Name || null,
            },
            PhotoExperiences: (
                collection?.PhotoExperiences || []
            ).map((experience) =>
                NormalizeAlbumExperience(
                    experience,
                    service
                )
            ),
        };
    });
}

/**
 * Normalizes one public media item for the current SUF components.
 *
 * @param {Object} item Public photo or video.
 * @returns {Object} Normalized media item.
 */
export function NormalizeMediaItem(item = {}) {
    const normalized = {
        ...item,
        VisitDate: ExtractDateYmd(
            item.VisitDate
        ),
        Tags: NormalizeTags(
            item.Tags ?? item.Tag
        ),
    };

    delete normalized.Tag;

    return normalized;
}

/**
 * Converts the unified GetMedia response to the internal view used by SUF components.
 *
 * @param {Object} response Public GetMedia response.
 * @returns {Object|Array<Object>} Photo page or video collection.
 */
export function NormalizeMediaResponse(
    response = {}
) {
    const media = (
        Array.isArray(response.Media)
            ? response.Media
            : []
    ).map(NormalizeMediaItem);

    if (Number(response.ContentType) === 1) {
        const videos = media.map((item) => ({
            ...item,
            ParkId: response.ParkId,
            VideoId: item.MediaId,
        }));

        videos.AlbumTags = NormalizeTags(
            response.AlbumTags
        );

        videos.TotalMedia = Number(
            response.TotalMedia ||
            media.length
        );

        videos.ContentType = Number(
            response.ContentType || 1
        );

        return videos;
    }

    return {
        ...response,
        AlbumTags: NormalizeTags(response.AlbumTags),
        TotalPhotos: Number(
            response.TotalMedia || 0
        ),
        Photos: media,
    };
}